import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "electrical-seo-colorado",
  metaTitle: "Electrical SEO in Colorado | Local SEO for Electricians",
  metaDescription:
    "Electrical SEO across Colorado — Map Pack rankings, panel-upgrade lead flow, and EV-charger install traffic. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Electrical marketing", to: "/electrical-contractor-marketing-colorado" },
  ],
  currentLabel: "Electrical SEO Colorado",
  eyebrow: "Electrical SEO — Colorado",
  h1: (
    <>
      Electrical <span className="text-brand-gold">SEO</span> in Colorado
    </>
  ),
  heroDescription:
    "Win the high-intent electrical searches that pay — panel upgrades, EV charger installs, generator installs, and emergency electrical repair across Colorado.",
  intro:
    "Most electrician SEO is generic — one 'electrician near me' page and hope. We build dedicated ranking pages for the high-ticket jobs that actually move the business: 200-amp panel upgrades, EV charger installs, generator installs, hot-tub circuits, knob-and-tube replacements, and commercial service. Each page targets the actual way Coloradans search and feeds into a properly tuned Google Business Profile.",
  definition: {
    term: "Electrical SEO in Colorado",
    body: "Electrical SEO is local search optimization that ranks electricians for high-intent jobs like 'panel upgrade Denver', 'EV charger install Colorado Springs', or 'generator installer near me' — through Map Pack work, service-line content, and review velocity.",
  },
  whatWeDo: [
    { title: "One ranking page per service line", body: "Panel upgrades, EV chargers, generators, hot tub circuits, rewires, commercial — each gets its own FAQ-rich page." },
    { title: "Map Pack work in every Colorado service area", body: "Colorado Springs, Denver, Fort Collins, Pueblo, Castle Rock — neighborhood-deep targeting." },
    { title: "EV-charger and solar-readiness content", body: "Colorado is one of the fastest-growing EV markets — we build content that captures the install searches early." },
    { title: "Review velocity tied to job completion", body: "Structured review-request flow tied to your FSM software so reviews come in steadily." },
    { title: "Schema for electrical trust signals", body: "LocalBusiness, Service, FAQPage, and Review schema — plus master electrician license, bonded/insured signals." },
    { title: "Mobile speed and conversion", body: "Sub-2-second load times, sticky click-to-call, after-hours emergency CTAs." },
  ],
  whyThisMarket: [
    {
      heading: "Colorado's EV adoption is creating a new search vertical",
      body: "EV charger install searches in Colorado have grown 400%+ in three years. Electricians who publish dedicated EV-charger content now own the vertical before it gets crowded.",
    },
    {
      heading: "Panel upgrades are the highest-intent residential search",
      body: "A homeowner searching 'panel upgrade Denver' is signaling a $2K–$5K job. Ranking for these terms beats ranking for 10x the volume of low-intent 'electrician' searches.",
    },
    {
      heading: "National lead-gen sites compete weakly in electrical",
      body: "Unlike HVAC and plumbing, the aggregator pressure is lighter in electrical — meaning well-optimized Colorado electricians can win city-level terms outright with the right content depth.",
    },
  ],
  relatedLinks: [
    { to: "/electrical-contractor-marketing-colorado", label: "Electrical contractor marketing — full hub" },
    { to: "/colorado-springs-seo", label: "How our SEO process works" },
    { to: "/colorado-springs-google-maps", label: "Local SEO & Google Maps overview" },
    { to: "/electrical-google-maps-colorado", label: "Electrical Google Maps optimization" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/denver-digital-marketing", label: "Denver digital marketing" },
    { to: "/case-studies", label: "Colorado contractor case studies" },
    { to: "/seo", label: "Our SEO service (all industries)" },
    { to: "/local-seo", label: "Local SEO overview" },
  ],
  faqs: [
    { q: "How long does electrical SEO take to work in Colorado?", a: "Most Colorado electricians see Map Pack movement in 60–90 days and stronger organic traffic by month 4–6. Electrical is less competitive than plumbing and HVAC, so well-built content can compound faster." },
    { q: "Should I focus on residential or commercial SEO?", a: "Both, but on separate pages. Residential and commercial buyers search differently and have different decision cycles. We typically build a residential-focused homepage path and a separate commercial section with its own ranking pages." },
    { q: "Is EV-charger install really worth a dedicated SEO page?", a: "Yes. EV install searches in Colorado are growing fast, the install jobs are profitable, and the competitive depth on those terms is still light. Building authority now means owning the vertical as it scales." },
    { q: "Do you only serve Colorado Springs electricians?", a: "No — we serve electrical contractors across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Boulder, Parker, and the Front Range." },
  ],
  serviceName: "Electrical SEO in Colorado",
  serviceDescription:
    "Local SEO and Map Pack optimization for electrical contractors across Colorado, with focus on panel upgrades and EV-charger installs.",
  areaServed: "Colorado",
  // SEO: consolidate ranking signal onto the /electrical-contractor-marketing-colorado flagship hub.
  canonicalOverride: "https://coloradowebimpressions.com/electrical-contractor-marketing-colorado",
};

export const Route = createFileRoute("/electrical-seo-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
